import { useCallback, useMemo, useRef, useState } from "react";
import { PopoverOrigin, Theme, useTheme } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { useAtomicStyles } from "styles";
import { useDispatch, useSelector } from "react-redux";
import { actions, selectors } from "../../redux-store";
import { useCurrentMerchantId, useTypedTranslations } from "../../hooks";
import {
  DateFilterTypes,
  StatsFilters,
} from "redux-store/slices/stats/interfaces";
import * as yup from "yup";
import moment from "moment";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuButton: {
      marginTop: 0,
      marginLeft: 0,
      padding: "5px 10px",
    },
    dateRangePaper: {
      background: "#fff",
      color: "black",
      width: 250,
    },
    datePickersPaper: {
      padding: 15,
      background: "#fff",
    },
    listItem: {
      height: 36,
    },
    pickersContainer: {
      display: "flex",
    },
    dateTextField: {
      width: 180,
    },
  }),
);

const useDateRangeFilter = () => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const classes = useMemo(
    () => ({
      dateRangePaper: {
        paper: styles.dateRangePaper,
      },
      datePickersPaper: {
        paper: styles.datePickersPaper,
      },
    }),
    [styles],
  );
  const atomicStyles = useAtomicStyles();
  const [t] = useTypedTranslations();
  const anchorRef = useRef();
  const [dateRangeMenuOpen, setDateRangeMenuOpen] = useState(false);
  const [customDateMenuOpen, setcustomDateMenuOpen] = useState(false);
  const statsFilters = useSelector(selectors.getStatsFilters);
  const dateFilterType = useSelector(selectors.getStatsDateFilterType);
  const dispatch = useDispatch();
  const merchantId = useCurrentMerchantId();

  const anchorOrigin = useMemo<PopoverOrigin>(
    () => ({ vertical: "bottom", horizontal: "left" }),
    [],
  );

  const transformOrigin = useMemo<PopoverOrigin>(
    () => ({ vertical: "top", horizontal: "left" }),
    [],
  );

  const dateRangeOptions = useMemo<{ desc: string; filter: DateFilterTypes }[]>(
    () => [
      { desc: "Ultimi 30 giorni", filter: DateFilterTypes.LAST_30_DAYS },
      { desc: "Ultimi 14 giorni", filter: DateFilterTypes.LAST_14_DAYS },
      { desc: "Ultimi 7 giorni", filter: DateFilterTypes.LAST_7_DAYS },
      { desc: "Mese precedente", filter: DateFilterTypes.PREVIOUS_MONTH },
      { desc: "Ultimi 90 giorni", filter: DateFilterTypes.LAST_90_DAYS },
      { desc: "Intervallo Personalizzato", filter: DateFilterTypes.CUSTOM },
    ],
    [],
  );

  const handleDateRangeMenuOpen = useCallback(
    () => setDateRangeMenuOpen(true),
    [],
  );
  const handleDateRangeMenuClose = useCallback(
    () => setDateRangeMenuOpen(false),
    [],
  );

  let filterSchema = yup.object().shape({
    [StatsFilters.START_DATE]: yup.mixed(),
    [StatsFilters.END_DATE]: yup
      .mixed()
      .test("isEndAfter", "End Date is Before Start", (value) => {
        return moment(value).isAfter(
          moment(yup.ref(StatsFilters.START_DATE).toString()),
        );
      }),
  });

  const handleMenuItemClick = useCallback(
    (dateFilterType: DateFilterTypes) => {
      dispatch(
        actions.setStatsDateFilterType({
          dateFilterType,
        }),
      );
      if (dateFilterType !== DateFilterTypes.CUSTOM) {
        dispatch(
          actions.triggerDevicesSubscribedAndReachedRequest({
            merchantId,
          }),
        );
      }
      setDateRangeMenuOpen(false);
    },
    [dispatch, merchantId],
  );

  const handleCustomDateMenuOpen = useCallback(() => {
    setDateRangeMenuOpen(false);
    setcustomDateMenuOpen(true);
  }, []);

  const handleCustomDateMenuClose = useCallback(
    () => setcustomDateMenuOpen(false),
    [],
  );

  const onChangeFilter = useCallback(
    (statsFilter: StatsFilters, value: string | number) => {
      dispatch(
        actions.setStatsDateFilterType({
          dateFilterType: DateFilterTypes.CUSTOM,
          [statsFilter]: value,
        }),
      );
    },
    [dispatch],
  );

  return {
    styles,
    classes,
    atomicStyles,
    t,
    anchorRef,
    anchorOrigin,
    transformOrigin,
    dateRangeMenuOpen,
    handleDateRangeMenuOpen,
    handleDateRangeMenuClose,
    customDateMenuOpen,
    handleCustomDateMenuOpen,
    handleCustomDateMenuClose,
    statsFilters,
    onChangeFilter,
    dateRangeOptions,
    handleMenuItemClick,
    dateFilterType,
  };
};

export { useDateRangeFilter as default };
